import React from "react";
import { AutoComplete } from "@progress/kendo-react-dropdowns";
import FabricCartItems from "./items.jsx";

/*
 * FabricCartComponent — the /Sample-Cart page (Lee-project port).
 * Props: data { items, fabrics }, fabricsRootUrl, max.
 * Server-side rendered: ChakraCore rules — no ?. / ?? syntax.
 */
export default class FabricCartComponent extends React.Component {
  constructor(props) {
    super(props);
    var data = props.data || {};
    this.state = {
      items: data.items || [],
      searchValue: ""
    };
    this.fabrics = data.fabrics || [];
  }

  dispatchCount = (count) => {
    try {
      document.dispatchEvent(new CustomEvent("update-fabric-cart-counter", { detail: count }));
    } catch (e) { /* SSR no-op */ }
  };

  toast = (text) => {
    try {
      var t = document.getElementById("fabric-cart-toast");
      if (t && typeof t.open === "function") {
        t.setAttribute("text", text);
        t.open();
      }
    } catch (e) { /* no-op */ }
  };

  post = (path, body) => {
    return fetch("/api/FabricCart/" + path, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body)
    }).then((r) => r.json());
  };

  refresh = () => {
    var self = this;
    fetch("/Sample-Cart", { headers: { Accept: "text/html" } });
    // Items are authoritative on the server; re-pull via the cart model is
    // heavier than needed — mutations below keep local state in sync instead.
    return self;
  };

  addBySku = (sku) => {
    var self = this;
    this.post("Add", { sku: sku }).then(function (data) {
      if (data && data.success) {
        // find catalog entry for local display
        var f = null;
        for (var i = 0; i < self.fabrics.length; i++) {
          if (self.fabrics[i].sku === sku) { f = self.fabrics[i]; break; }
        }
        var items = self.state.items.slice();
        items.push({
          sku: sku,
          name: f ? f.name : sku,
          image: f ? f.image : "",
          grade: f ? f.grade : "",
          itemType: f ? (f.itemType || "Fabric") : "Fabric",
          quantity: 1
        });
        self.setState({ items: items, searchValue: "" });
        self.dispatchCount(data.count);
        try {
          document.dispatchEvent(new CustomEvent("fabric-cart-added", {
            detail: { sku: sku, name: f ? f.name : sku }
          }));
        } catch (e) { /* no-op */ }
      } else {
        self.toast(data && data.message ? data.message : "Unable to add this fabric.");
      }
    }).catch(function () {
      self.toast("Unable to add this item right now.");
    });
  };

  // Autocomplete entries show "NAME - SKU" — many samples share a name
  // (e.g. Snow), so the number is the disambiguator. Finish SKUs ARE
  // their names, so finishes show "NAME - Finish" instead.
  displayName = (f) => (f.name || f.sku) + " - " + (f.itemType === "Finish" ? "Finish" : f.sku);

  onAddClick = () => {
    var typed = (this.state.searchValue || "").trim().toLowerCase();
    if (!typed) return;
    var match = null;
    for (var i = 0; i < this.fabrics.length; i++) {
      var f = this.fabrics[i];
      if (this.displayName(f).toLowerCase() === typed) { match = f; break; }
    }
    if (!match) {
      // fall back to a unique bare-name match (typed without the number)
      var byName = [];
      for (var k = 0; k < this.fabrics.length; k++) {
        if ((this.fabrics[k].name || "").toLowerCase() === typed) byName.push(this.fabrics[k]);
      }
      if (byName.length === 1) match = byName[0];
    }
    if (!match) {
      this.toast("Please select a fabric or finish from the list.");
      return;
    }
    // client-side dedupe for a friendlier message; server enforces anyway
    for (var j = 0; j < this.state.items.length; j++) {
      if (this.state.items[j].sku === match.sku) {
        this.toast("This item is already in your sample cart.");
        return;
      }
    }
    this.addBySku(match.sku);
  };

  onSearchKeyDown = (e) => {
    if (e.nativeEvent && e.nativeEvent.key === "Enter") this.onAddClick();
  };

  onQuantityChange = (item, value) => {
    var self = this;
    this.post("Update", { sku: item.sku, quantity: value }).then(function (data) {
      if (data && data.success) {
        var items = self.state.items.map(function (x) {
          return x.sku === item.sku ? { ...x, quantity: value } : x;
        });
        self.setState({ items: items });
        self.dispatchCount(data.count);
      } else {
        self.toast(data && data.message ? data.message : "Unable to update quantity.");
      }
    }).catch(function () { self.toast("Unable to update quantity right now."); });
  };

  onRemove = (item) => {
    var self = this;
    this.post("Delete", { sku: item.sku }).then(function (data) {
      if (data && data.success) {
        var items = self.state.items.filter(function (x) { return x.sku !== item.sku; });
        self.setState({ items: items });
        self.dispatchCount(data.count);
      } else {
        self.toast(data && data.message ? data.message : "Unable to remove this item.");
      }
    }).catch(function () { self.toast("Unable to remove this item right now."); });
  };

  render() {
    var items = this.state.items;
    var self = this;
    // Kendo AutoComplete does not filter its data itself — filter here
    // (contains, case-insensitive, capped) against "NAME - SKU".
    var typed = (this.state.searchValue || "").trim().toLowerCase();
    var names = [];
    if (typed.length > 0) {
      for (var i = 0; i < this.fabrics.length && names.length < 50; i++) {
        var dn = self.displayName(this.fabrics[i]);
        if (dn.toLowerCase().indexOf(typed) > -1) names.push(dn);
      }
    }

    return (
      <div className="fabric-cart-page">
        {/* caption limits are the configured program rules — the staff
            100-cap bypass never shows here (it feeds the qty dropdowns) */}
        <h4 className="fabric-cart-note">
          FABRICS (4"x4") &amp; LEATHERS (3.5"x2") LIMIT {this.props.captionMaxFabric || this.props.maxFabric || 10} &middot; FINISH (2"x2") LIMIT {this.props.captionMaxFinish || this.props.maxFinish || 2}
        </h4>

        <div className="fabric-cart-add-bar">
          <AutoComplete
            data={names}
            value={this.state.searchValue}
            onChange={(e) => this.setState({ searchValue: e.target.value })}
            onKeyDown={this.onSearchKeyDown}
            placeholder="Search fabrics, leathers, or finishes..."
            suggest={false}
            style={{ width: "320px" }}
          />
          <button className="ui-button-configure ink" onClick={this.onAddClick}>ADD</button>
        </div>

        <FabricCartItems
          items={items}
          imageRootUrl={this.props.imageRootUrl}
          maxFabric={this.props.maxFabric}
          maxFinish={this.props.maxFinish}
          onQuantityChange={this.onQuantityChange}
          onRemove={this.onRemove}
        />

        <div className="fabric-cart-actions">
          <a className="ui-button-configure ink" href="/Custom-Option/Fabric/All">ADD MORE FABRICS</a>
          <a className="ui-button-configure ink" href="/Custom-Option/Leather/All">ADD MORE LEATHERS</a>
          <a className="ui-button-configure ink" href="/Custom-Option/Finish">ADD MORE FINISHES</a>
          {items.length > 0 && (
            <a className="ui-button-configure ink fabric-cart-checkout-btn" href="/Sample-Cart/Checkout">PROCEED TO CHECKOUT</a>
          )}
        </div>
      </div>
    );
  }
}
